import { Trans } from '@lingui/macro';
import { useWeb3React } from '@web3-react/core';
import { getConnection, getConnectionName, getIsCoinbaseWallet, getIsMetaMask } from 'connection/utils';
import { useScreenSizeRatio } from 'hooks/useScreenSizeRatio';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToggleWalletModal } from 'state/application/hooks';
import { useAppDispatch } from 'state/hooks';
import { updatePhoneboothState, updateScreenCursorState } from 'state/phonebooth/slice';
import { useGetTwitterTokenMutation } from 'state/twitter/api';
import { updateSelectedWallet } from 'state/user/slice';
import { removeConnectedWallet } from 'state/wallets/slice';
import { shortenAddress } from 'utils';
import { isMobile } from 'utils/userAgent';

import { usePhoneboothContext } from '..';
import StatusRow from './StatusRow';
import * as S from './StatusScreen.styles';

type FloppyDiskState = 'idle' | 'generating' | 'generated';

const StatusScreen: React.FC = () => {
  const { onGenerateAccessDisk, onFinishedGenerating, onHideAccessDisk } = usePhoneboothContext();

  const dispatch = useAppDispatch();
  const sizeRatio = useScreenSizeRatio();

  const { account, ENSName, connector } = useWeb3React();

  const isMetaMask = getIsMetaMask();
  const isCoinbaseWallet = getIsCoinbaseWallet();
  const isInjectedMobileBrowser = (isMetaMask || isCoinbaseWallet) && isMobile;

  const toggleWalletModal = useToggleWalletModal();

  const navigate = useNavigate();

  const [floppydiskState, setFloppydiskState] = useState<FloppyDiskState>('idle');

  const [getTwitterToken] = useGetTwitterTokenMutation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Check for params coming from twitter or discord callback
  useEffect(() => {
    console.log('Checking search params');
    const state = searchParams.get('state');
    console.log(state);

    // callback from twitter
    if (state === 'twitter') {
      const twitterCode = searchParams.get('code');

      if (twitterCode) {
        getTwitterToken({ authCode: twitterCode });
        console.log(`set ${twitterCode}`);
      }
      // setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <S.StatusScreenWrapper>
      <S.Header sizeRatio={sizeRatio}>
        <S.Title sizeRatio={sizeRatio}>
          <Trans>Status</Trans>
        </S.Title>
        <S.Back
          sizeRatio={sizeRatio}
          onBack={() => {
            dispatch(updatePhoneboothState('home'));
            onHideAccessDisk();
            navigate('/home');
          }}
        />
      </S.Header>
      <S.Content sizeRatio={sizeRatio}>
        <StatusRow
          title="Twitter"
          isConnected={false}
          onConnect={function (): void {
            window.location.href = `https://twitter.com/i/oauth2/authorize?response_type=code&client_id=${
              process.env.REACT_APP_TWITTER_CLIENT_ID
            }&redirect_uri=${encodeURIComponent(
              `${window.location.protocol}//${window.location.hostname}${
                window.location.port ? `:${window.location.port}` : ''
              }/status/`
            )}&scope=tweet.read%20users.read%20offline.access&state=twitter&code_challenge=challenge&code_challenge_method=plain`;
          }}
          onDisconnect={function (): void {
            throw new Error('Function not implemented.');
          }}
        />
        <StatusRow
          title="Discord"
          isConnected={false}
          onConnect={function (): void {
            throw new Error('Function not implemented.');
          }}
          onDisconnect={function (): void {
            throw new Error('Function not implemented.');
          }}
        />
        <StatusRow
          title="Wallet"
          isConnected={!!account}
          username={ENSName || (account ? shortenAddress(account) : undefined)}
          onConnect={() => {
            toggleWalletModal();
          }}
          onDisconnect={() => {
            if (!isInjectedMobileBrowser) {
              const walletType = getConnectionName(getConnection(connector).type, getIsMetaMask());
              if (connector.deactivate) {
                connector.deactivate();
              } else {
                connector.resetState();
              }

              dispatch(updateSelectedWallet({ wallet: undefined }));
              dispatch(removeConnectedWallet({ account, walletType }));
            }
          }}
          hideDisconnect={isInjectedMobileBrowser}
        />
      </S.Content>
      <S.StatusActionWrapper sizeRatio={sizeRatio}>
        {floppydiskState === 'idle' && (
          <S.GeneratebtnWrapper>
            <S.GenerateBtn
              sizeRatio={sizeRatio}
              type="button"
              onClick={() => {
                setFloppydiskState('generating');
                onGenerateAccessDisk(ENSName || (account ? shortenAddress(account) : ''));
                setTimeout(() => onFinishedGenerating(), 3200);
              }}
              onMouseEnter={() => dispatch(updateScreenCursorState('pointer'))}
              onMouseLeave={() => dispatch(updateScreenCursorState('hover'))}
            >
              <Trans>Generate Access Disk</Trans>
            </S.GenerateBtn>
          </S.GeneratebtnWrapper>
        )}
        {floppydiskState === 'generating' && (
          <S.GeneratingWrapper>
            <S.FloppyDisk />
            <S.ProgressWrapper>
              <S.GeneratingLabel>
                <Trans>Generating Access Disk</Trans>
              </S.GeneratingLabel>
              <S.LoadingBarWrapper sizeRatio={sizeRatio}>
                <S.LoadingBar />
              </S.LoadingBarWrapper>
            </S.ProgressWrapper>
          </S.GeneratingWrapper>
        )}
      </S.StatusActionWrapper>
    </S.StatusScreenWrapper>
  );
};

export default StatusScreen;
